/**
 * AutoGlow · Advisor · State components
 * Loading · Empty · Error · Partial.
 * Used by EVERY widget · same pattern from backend "Información insuficiente".
 */
import React from "react";
import { Loader2, AlertCircle, Info, AlertTriangle } from "lucide-react";

export function LoadingState({ label = "Cargando inteligencia…", testId }) {
  return (
    <div
      data-testid={testId || "loading-state"}
      className="flex items-center gap-2 p-4 text-sm text-[var(--ag-text-dim)]"
      role="status"
      aria-live="polite"
    >
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({
  title = "Información insuficiente.",
  message = "Aún no hay datos suficientes para mostrar este bloque.",
  testId,
}) {
  return (
    <div
      data-testid={testId || "empty-state"}
      className="flex flex-col items-start gap-1 rounded-md border border-dashed border-[var(--ag-border)] bg-[var(--ag-surface-2)] p-4 text-sm"
    >
      <div className="flex items-center gap-2 text-[var(--ag-text)]">
        <Info className="h-4 w-4 text-[var(--ag-text-dim)]" aria-hidden="true" />
        <span className="font-medium">{title}</span>
      </div>
      <p className="text-xs text-[var(--ag-text-dim)]">{message}</p>
    </div>
  );
}

export function ErrorState({
  title = "No se pudo cargar.",
  message = "Inténtalo de nuevo en unos segundos.",
  onRetry,
  testId,
}) {
  return (
    <div
      data-testid={testId || "error-state"}
      className="flex flex-col items-start gap-1 rounded-md border border-red-500/40 bg-red-500/5 p-4 text-sm"
      role="alert"
    >
      <div className="flex items-center gap-2 text-red-500">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        <span className="font-medium">{title}</span>
      </div>
      <p className="text-xs text-[var(--ag-text-dim)]">{message}</p>
      {onRetry && (
        <button
          data-testid="error-state-retry"
          type="button"
          onClick={onRetry}
          className="mt-2 rounded border border-[var(--ag-border)] px-2 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
        >
          Reintentar
        </button>
      )}
    </div>
  );
}

export function PartialState({ message = "Datos parciales · algunos agregadores no respondieron.", testId }) {
  return (
    <div
      data-testid={testId || "partial-state"}
      className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-amber-500"
      role="status"
    >
      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

/**
 * Single dispatcher · render the right state based on React Query shape.
 *
 * Usage:
 *   <QueryGate query={query}>{(data) => <Widget data={data} />}</QueryGate>
 */
export function QueryGate({ query, children, loadingLabel, emptyMessage, testId }) {
  if (query.isLoading) return <LoadingState label={loadingLabel} testId={testId && `${testId}-loading`} />;
  if (query.error) {
    const status = query.error?.response?.status;
    if (status === 403) {
      return (
        <EmptyState
          title="Acceso restringido"
          message="Tu rol no tiene acceso a este bloque."
          testId={testId && `${testId}-forbidden`}
        />
      );
    }
    if (status === 404) {
      return (
        <EmptyState
          title="Información insuficiente."
          message="No hay datos para los parámetros indicados."
          testId={testId && `${testId}-empty`}
        />
      );
    }
    return <ErrorState onRetry={() => query.refetch()} testId={testId && `${testId}-error`} />;
  }
  if (!query.data) return <EmptyState message={emptyMessage} testId={testId && `${testId}-empty`} />;
  return children(query.data);
}
